import type { EmailSend, EmailSummary, SplitArm } from "@/modules/cvce_marketing/api";
import { dateRange } from "@/modules/cvce_marketing/format";

import { formatCount } from "./format";
import { formatMoney, formatOrders } from "./stats-format";

/**
 * Precision Funnel C9 R2 (KET-7214). The Email tab reads cvce's email endpoints, the
 * same ones CVCE Marketing reads, cut to one funnel. Nothing here adds, divides or
 * re-credits an order: every figure is the one cvce served (system spec S10, HARD 3).
 */
export function funnelEmailQuery(slug: string | null, days: number, today: Date = new Date()): string {
  const params = new URLSearchParams(dateRange(days, today));
  // No funnel = every funnel: cvce sums across them itself (GATE-368 FN-1), the portal adds nothing.
  if (slug !== null) params.set("funnel", slug);
  return `?${params.toString()}`;
}

export const funnelEmailKey = (name: string, q: string) => ["funnels", "email", name, q] as const;

/** Send rows by broadcast, so a split-test arm reads its orders from cvce's own send row. */
export function sendsById(sends: EmailSend[]): Map<string, EmailSend> {
  return new Map(sends.map((s) => [s.broadcast_id, s]));
}

/** An arm is one broadcast: its orders and revenue are that send's, or unknown. */
export function armOrders(
  arm: Pick<SplitArm, "broadcast_id">,
  byId: Map<string, EmailSend>,
): { orders: number | null; revenue: number | null } {
  const row = byId.get(arm.broadcast_id);
  return { orders: row?.orders_attributed ?? null, revenue: row?.revenue_attributed_gbp ?? null };
}

/** The Email tab's All: every send cvce served for the window, funnel-stamped or not (GATE-368 FN-1). */
export const ALL_SENDS_LABEL = "All sends";
export const ALL_SENDS_CAPTION = "All sends counts every send in the window, including sends not stamped to a funnel.";

/** The email totals in one sentence, every figure as cvce sent it. `all` = no funnel was named. */
export function emailSummaryLine(summary: EmailSummary | null, name: string, days: number, all = false): string {
  if (!summary || summary.sends === 0) {
    return `${name}, last ${days} days: ${all ? "no sends" : "no sends stamped to this funnel"}.`;
  }
  const orders = summary.orders_attributed ?? null;
  const parts = [
    `${formatCount(summary.sends)} ${summary.sends === 1 ? "send" : "sends"}`,
    summary.recipients == null ? null : `${formatCount(summary.recipients)} recipients`,
    `${formatOrders(orders)} ${orders === 1 ? "order" : "orders"}`,
    formatMoney(summary.revenue_attributed_gbp),
  ].filter(Boolean);
  return `${name}, last ${days} days: ${parts.join(" · ")}`;
}
